import { useEffect, useState } from "react";
import { Link } from "react-router";
import { Mic, MicOff, Phone, PhoneOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

import { getSupabaseClient } from "../providers/supabase/supabase";
import { useSoftphone } from "./SoftphoneContext";

interface CallerInfo {
  label: string;
  link: string | null;
}

const last10 = (value: string) => value.replace(/\D/g, "").slice(-10);

async function lookupCaller(number: string): Promise<CallerInfo> {
  const key = last10(number);
  const fallback = { label: formatNumber(number), link: null };
  if (key.length < 10) return fallback;
  try {
    const { data } = await getSupabaseClient().rpc("crm_match_phone", {
      p_digits: key,
    });
    if (data?.master_id) {
      const { data: master } = await getSupabaseClient()
        .from("masters")
        .select("name")
        .eq("id", data.master_id)
        .maybeSingle();
      return {
        label: master?.name ? `Мастер: ${master.name}` : fallback.label,
        link: `/masters/${data.master_id}/show`,
      };
    }
    if (data?.contact_id) {
      const { data: contact } = await getSupabaseClient()
        .from("contacts")
        .select("first_name, last_name")
        .eq("id", data.contact_id)
        .maybeSingle();
      const name = [contact?.first_name, contact?.last_name]
        .filter(Boolean)
        .join(" ");
      return {
        label: name || fallback.label,
        link: `/contacts/${data.contact_id}/show`,
      };
    }
  } catch {
    // lookup is best-effort
  }
  return fallback;
}

function formatNumber(value: string | null): string {
  if (!value) return "";
  const digits = value.replace(/\D/g, "");
  if (digits.length === 11) {
    return `+7 ${digits.slice(1, 4)} ${digits.slice(4, 7)}-${digits.slice(7, 9)}-${digits.slice(9)}`;
  }
  return value;
}

const useCallTimer = (active: boolean) => {
  const [seconds, setSeconds] = useState(0);
  useEffect(() => {
    if (!active) {
      setSeconds(0);
      return;
    }
    const timer = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => clearInterval(timer);
  }, [active]);
  const mm = String(Math.floor(seconds / 60)).padStart(2, "0");
  const ss = String(seconds % 60).padStart(2, "0");
  return `${mm}:${ss}`;
};

export const SoftphoneUI = () => {
  const { status, remoteNumber, answer, decline, hangup, toggleMute } =
    useSoftphone();
  const [caller, setCaller] = useState<CallerInfo>({ label: "", link: null });
  const [muted, setMuted] = useState(false);
  const timer = useCallTimer(status === "in-call");

  useEffect(() => {
    if (remoteNumber) {
      lookupCaller(remoteNumber).then(setCaller);
    } else {
      setCaller({ label: "", link: null });
    }
    if (status === "ready") setMuted(false);
  }, [remoteNumber, status]);

  if (status === "ringing-in") {
    return (
      <Card className="fixed top-16 right-4 z-50 w-80 shadow-xl border-green-600 animate-pulse-slow py-4">
        <CardContent className="px-4 flex flex-col gap-3">
          <div>
            <p className="text-xs text-muted-foreground">Входящий звонок</p>
            <p className="text-base font-semibold">
              {caller.link ? (
                <Link to={caller.link} className="underline">
                  {caller.label}
                </Link>
              ) : (
                caller.label
              )}
            </p>
            <p className="text-sm text-muted-foreground">
              {formatNumber(remoteNumber)}
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              className="flex-1 bg-green-600 hover:bg-green-700"
              onClick={answer}
            >
              <Phone className="w-4 h-4 mr-1" /> Принять
            </Button>
            <Button variant="destructive" className="flex-1" onClick={decline}>
              <PhoneOff className="w-4 h-4 mr-1" /> Отклонить
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (status === "calling" || status === "in-call") {
    return (
      <Card className="fixed bottom-4 right-4 z-50 w-80 shadow-xl py-3">
        <CardContent className="px-4 flex items-center gap-3">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">
              {caller.link ? (
                <Link to={caller.link} className="underline">
                  {caller.label}
                </Link>
              ) : (
                caller.label || formatNumber(remoteNumber)
              )}
            </p>
            <p className="text-xs text-muted-foreground">
              {status === "calling" ? "Вызов..." : timer}
            </p>
          </div>
          {status === "in-call" && (
            <Button
              variant="outline"
              size="icon"
              className="h-9 w-9"
              onClick={() => setMuted(toggleMute())}
              aria-label="Mute"
            >
              {muted ? (
                <MicOff className="w-4 h-4" />
              ) : (
                <Mic className="w-4 h-4" />
              )}
            </Button>
          )}
          <Button
            variant="destructive"
            size="icon"
            className="h-9 w-9"
            onClick={hangup}
            aria-label="Hang up"
          >
            <PhoneOff className="w-4 h-4" />
          </Button>
        </CardContent>
      </Card>
    );
  }

  return null;
};
